import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './SurveyGate.css';
import { APP_CONFIG } from "../config/appConfig";
import { AuthContext } from "../context/AuthContext";
import tracker from '../utils/tracker';

// Social logos
import tiktokLogo from '../assets/survey/tiktok.png';
import instagramLogo from '../assets/survey/instagram.png';
import telegramLogo from '../assets/survey/telegram.png';
import discordLogo from '../assets/survey/discord.png';
import friendsLogo from '../assets/survey/friends.png';
import otherLogo from '../assets/survey/other.png';

const BASE_URL = APP_CONFIG.API_BASE_URL;

const SurveyModal = ({ visible }) => {
  const navigate = useNavigate();
  const { refreshAuth } = useContext(AuthContext);
  const [selectedSource, setSelectedSource] = useState(null);
  const [otherText, setOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Track when the onboarding survey is shown (drop-off vs survey_submitted)
  useEffect(() => {
    if (visible) tracker.track('survey_opened');
  }, [visible]);

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (visible) {
      // Store current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY); // restore scroll position
      };
    }
  }, [visible]);


  const surveyOptions = [
    { id: 'tiktok', label: 'TikTok', image: tiktokLogo, size: 36 },
    { id: 'instagram', label: 'Instagram', image: instagramLogo, size: 36 },
    { id: 'telegram', label: 'Telegram', image: telegramLogo, size: 32 },
    { id: 'discord', label: 'Discord', image: discordLogo, size: 36 },
    { id: 'friends', label: 'Friends', image: friendsLogo, size: 30 },
    { id: 'other', label: 'Other', image: otherLogo, size: 30 },
  ];

  const handleSubmit = async () => {
    if (!selectedSource || (selectedSource === 'other' && !otherText.trim())) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/survey/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          source: selectedSource,
          otherText: selectedSource === 'other' ? otherText : ''
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to submit survey');
        setSubmitting(false);
        return;
      }

      // Success - refresh auth to update user context, then navigate
      await refreshAuth();
      navigate('/purchase');
    } catch (error) {
      console.error('Error submitting survey:', error);
      alert('Failed to submit survey. Please try again.');
      setSubmitting(false);
    }
  };


  // Don't render anything if not visible
  if (!visible) return null;

  return (
    <div className="survey-overlay">
      <div className="survey-modal">
        <div className="survey-content">
          <h2>How did you hear about LunaSearch?</h2>

          <div className="survey-options">
            {surveyOptions.map(option => (
              <div
                key={option.id}
                className={`survey-option ${selectedSource === option.id ? 'selected' : ''}`}
                onClick={() => setSelectedSource(option.id)}
              >
                <div
                    className="survey-icon-wrapper"
                    style={{ width: option.size, height: option.size }}
                  >
                    <img
                      src={option.image}
                      alt={option.label}
                      style={{ width: option.size, height: option.size, objectFit: 'contain' }}
                    />
                  </div>

                <span className="survey-option-text">{option.label}</span>
              </div>
            ))}
          </div>

          {selectedSource === 'other' && (
            <textarea
              className="survey-textarea"
              placeholder="Tell us more..."
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
            />
          )}
        </div>

        <div className="survey-button-wrapper">
          <button
            className="survey-submit"
            disabled={!selectedSource || (selectedSource === 'other' && !otherText.trim()) || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Submitting...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyModal;